import { useCallback, useEffect, useRef, useState } from 'react';
import { Eraser } from 'lucide-react';
import { Button } from '@/components/ui';

const HEIGHT = 180;
const INK = '#111827';

/**
 * Drawn-signature capture with pointer events, so touch, pen, and mouse all
 * work. Reports a PNG data URI after each stroke, or `null` once cleared. The
 * canvas is sized to its container at device pixel ratio for crisp strokes.
 */
export function SignaturePad({
  onChange,
  disabled = false,
  label = 'Signature',
}: {
  onChange: (dataUri: string | null) => void;
  disabled?: boolean;
  label?: string;
}): React.ReactElement {
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const drawing = useRef(false);
  const last = useRef<{ x: number; y: number } | null>(null);
  const [hasInk, setHasInk] = useState(false);

  const context = useCallback((): CanvasRenderingContext2D | null => {
    const canvas = canvasRef.current;
    return canvas ? canvas.getContext('2d') : null;
  }, []);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    // The canvas is 100% wide in CSS; its pixel buffer follows the rendered
    // width so strokes stay crisp without ever forcing the layout wider.
    const ratio = window.devicePixelRatio || 1;
    const width = canvas.clientWidth || 320;
    canvas.width = Math.round(width * ratio);
    canvas.height = Math.round(HEIGHT * ratio);
    const ctx = context();
    if (!ctx) return;
    ctx.scale(ratio, ratio);
    ctx.lineWidth = 2.2;
    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';
    ctx.strokeStyle = INK;
  }, [context]);

  const point = (event: React.PointerEvent<HTMLCanvasElement>) => {
    const rect = event.currentTarget.getBoundingClientRect();
    return { x: event.clientX - rect.left, y: event.clientY - rect.top };
  };

  const start = (event: React.PointerEvent<HTMLCanvasElement>): void => {
    if (disabled) return;
    event.preventDefault();
    event.currentTarget.setPointerCapture?.(event.pointerId);
    drawing.current = true;
    last.current = point(event);
    const ctx = context();
    if (ctx && last.current) {
      ctx.beginPath();
      ctx.arc(last.current.x, last.current.y, 1.1, 0, Math.PI * 2);
      ctx.fillStyle = INK;
      ctx.fill();
    }
  };

  const move = (event: React.PointerEvent<HTMLCanvasElement>): void => {
    if (!drawing.current || disabled) return;
    event.preventDefault();
    const next = point(event);
    const ctx = context();
    if (ctx && last.current) {
      ctx.beginPath();
      ctx.moveTo(last.current.x, last.current.y);
      ctx.lineTo(next.x, next.y);
      ctx.stroke();
    }
    last.current = next;
  };

  const end = (): void => {
    if (!drawing.current) return;
    drawing.current = false;
    last.current = null;
    setHasInk(true);
    const canvas = canvasRef.current;
    onChange(canvas ? canvas.toDataURL('image/png') : null);
  };

  const clear = (): void => {
    const canvas = canvasRef.current;
    const ctx = context();
    if (canvas && ctx) {
      ctx.save();
      ctx.setTransform(1, 0, 0, 1, 0, 0);
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      ctx.restore();
    }
    setHasInk(false);
    onChange(null);
  };

  return (
    <div>
      <div className="relative w-full overflow-hidden rounded-lg border border-gray-300 bg-white dark:border-gray-600">
        <canvas
          ref={canvasRef}
          role="img"
          aria-label={`${label}: draw with your finger, pen, or mouse`}
          className={`block w-full touch-none ${disabled ? 'cursor-not-allowed opacity-60' : 'cursor-crosshair'}`}
          style={{ height: HEIGHT }}
          onPointerDown={start}
          onPointerMove={move}
          onPointerUp={end}
          onPointerLeave={end}
          onPointerCancel={end}
        />
        {!hasInk && (
          <p
            aria-hidden
            className="pointer-events-none absolute inset-x-0 bottom-3 text-center text-xs text-gray-400"
          >
            Sign here
          </p>
        )}
        <div
          aria-hidden
          className="pointer-events-none absolute inset-x-6 bottom-9 border-b border-dashed border-gray-300"
        />
      </div>
      <div className="mt-2 flex justify-end">
        <Button
          type="button"
          variant="ghost"
          size="sm"
          onClick={clear}
          disabled={disabled || !hasInk}
        >
          <Eraser className="mr-1.5 h-4 w-4" aria-hidden />
          Clear
        </Button>
      </div>
    </div>
  );
}
